import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Dispatch } from "redux"
import { loadStripe } from "@stripe/stripe-js"

import {
    LANG_EN,
    LANGUAGES,
    LOCAL_STORAGE_LANG_ID,
    LOCAL_STORAGE_LANG_REPORT_ID,
    LOCAL_STORAGE_USER_ID,
    LOCAL_STORAGE_SESSION_ID,
    LOCAL_STORAGE_SHOW_PACKAGE_EXPIRED,
} from "const"
import { LangIndex, OnlineUsers, replaceHistoryState } from "client/utils"
import {
    StoreType,
    UserData,

    LoadLang,
    LoadStripeKey,
    LoadAccountPackages,
    LoadAccountPackageUsage,
    LoadAccountPermissions,
    LoadAccountSettings,
    LoadUserSettings,
    LoadUserGroups,
    LoadAllPackages,
    LoadAllAccounts,
    LoadAllModules,
    LoadAllUsers,
    LoadAllTags,

    GoogleConnect,
    MicrosoftConnect,

    setSystemLang,
    setSystemLangReport,
    setBillingStripePromise,
    setSystemPackageActive,
    setSystemPackageSettings,
    setSystemPackageUsage,
    setSystemAccountPermissions,
    setSystemAccountSettings,
    setSystemUserSettings,
    setUserSettings,
    setSystemUserData,
    setSystemUserGroups,
    clearSystemUserData,
    setPackageList,
    setAccountList,
    setAccountIds,
    setModuleList,
    setUserList,
    setOnlineUsers,
    setTagList,
    setSystemLostServerConnection,

    initPackageSettings,
    initPackageUsage,
} from "client/store"

import { Layout } from "client/parts/layout"
import { Loader, Switch, Route } from "client/components"

import * as routes from "client/parts/routes"
import Login from "client/parts/auth/login"
import Register from "client/parts/auth/register"
import ConfirmEmail from "client/parts/auth/confirmEmail"
import ForgotPassword from "client/parts/auth/forgotPassword"
import ResetPassword from "client/parts/auth/resetPassword"
import App from "client/parts/app"

import {
    getServerUserData,
    listenSocketSetUserData,
    listenSocketClearUserData,
    listenSocketOnlineUsers,
    listenSocketConnectionError,
} from "client/socketApi"

interface Location {
    hash: string
    pathname: string
    search: string
}
interface LocationState {
    from: Location
}

const openUrls = [
    routes.loginRoute,
    routes.registerRoute,
    routes.confirmEmailRoute,
    routes.forgotPasswordRoute,
    routes.resetPasswordRoute,
]

const Main = () => {
    const [isAuthorized, setIsAuthorized] = useState(undefined)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    useEffect(() => {
        if (lang) document.title = lang.businessMaster
        else {
            let langId = window.localStorage.getItem(LOCAL_STORAGE_LANG_ID) as LangIndex
            if (!langId || !LANGUAGES.includes(langId)) langId = LANG_EN

            let langReportId = window.localStorage.getItem(LOCAL_STORAGE_LANG_REPORT_ID) as LangIndex
            if (!langReportId || !LANGUAGES.includes(langReportId)) langReportId = LANG_EN

            LoadLang(langId).then((res) => dispatch(setSystemLang(res)))
            LoadLang(langReportId).then((res) => dispatch(setSystemLangReport(res)))
        }
    }, [lang])

    useEffect(() => {
        LoadStripeKey().then((publicKey) => {
            dispatch(setBillingStripePromise(loadStripe(publicKey)))
        })
    }, [])

    useEffect(() => {
        const userId = parseInt(window.localStorage.getItem(LOCAL_STORAGE_USER_ID), 10) || null
        const sessionId = window.localStorage.getItem(LOCAL_STORAGE_SESSION_ID) || null

        // get user data on page load, if user was previously authorized
        if (userId && sessionId) {
            getServerUserData(userId, sessionId, (userData: UserData) => {
                if (userData) LoadDataOnLogin(userData, dispatch, setIsAuthorized)
                else {
                    window.localStorage.removeItem(LOCAL_STORAGE_SHOW_PACKAGE_EXPIRED)
                    setIsAuthorized(false)
                }
            })
        } else {
            window.localStorage.removeItem(LOCAL_STORAGE_SHOW_PACKAGE_EXPIRED)
            setIsAuthorized(false)
        }

        // set user data after successful login
        listenSocketSetUserData((userData: UserData) => {
            LoadDataOnLogin(userData, dispatch, setIsAuthorized)
        })

        listenSocketClearUserData(() => {
            // clear rendered components with loader
            setIsAuthorized(undefined)
            setTimeout(() => {
                setIsAuthorized(false)
            }, 0)

            dispatch(setAccountList(null))
            dispatch(setAccountIds(null))
            dispatch(clearSystemUserData())

            window.localStorage.removeItem(LOCAL_STORAGE_USER_ID)
            window.localStorage.removeItem(LOCAL_STORAGE_SESSION_ID)
            window.localStorage.removeItem(LOCAL_STORAGE_SHOW_PACKAGE_EXPIRED)
        })

        listenSocketOnlineUsers((users: OnlineUsers) => {
            dispatch(setOnlineUsers(users))
        })

        listenSocketConnectionError(() => {
            dispatch(setSystemLostServerConnection())
        })
    }, [])

    const { location, history } = window
    let locationState: LocationState

    if (history.state) {
        locationState = history.state as LocationState
    } else {
        locationState = {
            from: {
                hash: location.hash,
                pathname: location.pathname,
                search: location.search,
            },
        }
    }


    const pathSplitted = location.pathname.split("/")
    const currentRoute = pathSplitted.length > 1 ? `/${pathSplitted[1]}` : "/"

    if (!lang || isAuthorized === undefined) {
        return (
            <Layout>
                <Loader paddingTop={250} />
            </Layout>
        )
    }
    if (isAuthorized && openUrls.includes(currentRoute)) {
        const { from } = locationState
        // don't allow to visit open urls after user is authorized
        if (from && !openUrls.includes(from.pathname)) replaceHistoryState(`${from.pathname}${from.search}${from.hash}`, locationState)
        else replaceHistoryState("/")
    }
    if (!isAuthorized && !openUrls.includes(currentRoute)) {
        replaceHistoryState(routes.loginRoute, locationState)
    }
    return (
        <Switch>
            <Route exact path={routes.loginRoute} component={Login} />
            <Route exact path={routes.registerRoute} component={Register} />
            <Route exact path={routes.forgotPasswordRoute} component={ForgotPassword} />

            <Route exact path={routes.confirmEmailRoute} component={ConfirmEmail} />
            <Route
                path={`${routes.confirmEmailRoute}/:confirmationCode`}
                component={ConfirmEmail}
            />

            <Route exact path={routes.resetPasswordRoute} component={ResetPassword} />
            <Route
                path={`${routes.resetPasswordRoute}/:confirmationCode`}
                component={ResetPassword}
            />

            <Route path="/" component={App} />
        </Switch>
    )
}
export default Main


function LoadDataOnLogin(
    userData: UserData,
    dispatch: Dispatch,
    setIsAuthorized: (value: boolean) => void,
) {
    // show loader
    setIsAuthorized(undefined)

    const { userId, accountId } = userData
    const promiseArray = [
        // account and user data
        LoadAccountPackages(accountId)
        .then((accountPackages) => (
            LoadAllPackages()
            .then((packageList) => {
                const activePackage = accountPackages.length > 0 ? accountPackages[0] : null
                const { packageId } = activePackage || {}
                const packageSettings = packageList[packageId] ? packageList[packageId].settingsParsed : initPackageSettings

                dispatch(setPackageList(packageList))
                dispatch(setSystemPackageActive(activePackage))
                dispatch(setSystemPackageSettings(packageSettings))
            })
        )),
        LoadAccountPackageUsage(accountId).then((res) => {
            // Super Admin has no packages
            const packageUsage = accountId === 1 ? initPackageUsage : res.packageUsage
            dispatch(setSystemPackageUsage(packageUsage))
        }),
        LoadAccountPermissions(accountId).then((res) => {
            dispatch(setSystemAccountPermissions(res.accountPermissions))
        }),
        LoadAccountSettings(accountId).then((accountSettings) => {
            dispatch(setSystemAccountSettings(accountSettings))

            const nestedPromiseArray: Promise<any>[] = []
            if (accountSettings.drive.googleDriveConnected) {
                nestedPromiseArray.push(GoogleConnect({ accountId }))
            }
            if (accountSettings.drive.microsoftDriveConnected) {
                nestedPromiseArray.push(MicrosoftConnect({ accountId }))
            }

            return Promise.all(nestedPromiseArray)
        }),
        LoadUserSettings(userId, accountId).then((userSettings) => {
            dispatch(setSystemUserSettings(userSettings))
            dispatch(setUserSettings(userSettings))
        }),
        LoadUserGroups(userId, accountId).then((groupIds) => {
            dispatch(setSystemUserGroups(groupIds))
        }),

        // dictionaries
        LoadAllAccounts().then((accountList) => {
            dispatch(setAccountList(accountList))
        }),
        LoadAllModules().then((moduleList) => {
            dispatch(setModuleList(moduleList))
        }),
        LoadAllUsers().then((userList) => {
            dispatch(setUserList(userList))
        }),
        LoadAllTags().then((res) => {
            dispatch(setTagList(res.tagList))
        }),
    ]
    Promise.all(promiseArray)
    .then(() => {
        dispatch(setSystemUserData(userData))
        setIsAuthorized(true)
    })
}
